import type { SearchQuery } from "../../api/hooks";
import type { SearchDepth, SearchMode } from "../../api/types";

const MODES: SearchMode[] = ["hybrid", "fts", "semantic", "exact"];
const SORTS = ["relevance", "date_desc", "date_asc"] as const;

/** Any filter beyond the query text (what the empty-state hint and "clear filters" refer to). */
export function hasActiveFilters(q: SearchQuery): boolean {
  return q.company.length > 0 || q.type.length > 0 || !!q.from || !!q.to || q.superseded;
}

/** The query with every filter reset (page 1); the text, mode, depth and sort stay. */
export function clearedFilters(q: SearchQuery): SearchQuery {
  return { ...q, company: [], type: [], from: null, to: null, superseded: false, page: 1 };
}

/** The search form lives in the URL so results can be shared and the back button works. */
export function parseSearchParams(params: URLSearchParams): SearchQuery {
  const ints = (key: string) =>
    params
      .getAll(key)
      .map((v) => Number(v))
      .filter((n) => Number.isInteger(n) && n > 0);
  const mode = params.get("mode");
  const page = Number(params.get("page") ?? "1");
  return {
    q: params.get("q") ?? "",
    company: ints("company"),
    type: ints("type"),
    from: params.get("from"),
    to: params.get("to"),
    mode: MODES.includes(mode as SearchMode) ? (mode as SearchMode) : "hybrid",
    depth: params.get("depth") === "deep" ? "deep" : ("normal" as SearchDepth),
    page: Number.isInteger(page) && page > 0 ? page : 1,
    superseded: params.get("superseded") === "1",
    sort: (SORTS as readonly string[]).includes(params.get("sort") ?? "")
      ? (params.get("sort") as SearchQuery["sort"])
      : "relevance",
  };
}

export function serializeSearchParams(q: SearchQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.q) p.set("q", q.q);
  for (const c of q.company) p.append("company", String(c));
  for (const t of q.type) p.append("type", String(t));
  if (q.from) p.set("from", q.from);
  if (q.to) p.set("to", q.to);
  if (q.mode !== "hybrid") p.set("mode", q.mode);
  if (q.depth === "deep") p.set("depth", "deep");
  if (q.page > 1) p.set("page", String(q.page));
  if (q.superseded) p.set("superseded", "1");
  if (q.sort && q.sort !== "relevance") p.set("sort", q.sort);
  return p;
}

const HISTORY_KEY = "audit.search.history";
const HISTORY_MAX = 10;

export function readHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function pushHistory(q: string): string[] {
  const next = [q, ...readHistory().filter((x) => x !== q)].slice(0, HISTORY_MAX);
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  return next;
}

const FILTERS_KEY = "audit.search.filters";

/** Filters worth remembering between visits (not the query text or the page). */
export type SavedFilters = Pick<
  SearchQuery,
  "company" | "type" | "from" | "to" | "superseded" | "mode" | "sort"
>;

export function readSavedFilters(): SavedFilters | null {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return null;
    const p = parseSearchParams(new URLSearchParams(raw));
    return {
      company: p.company,
      type: p.type,
      from: p.from,
      to: p.to,
      superseded: p.superseded,
      mode: p.mode,
      sort: p.sort,
    };
  } catch {
    return null;
  }
}

/** The part of a query worth remembering, serialised; "" when it is all defaults. */
export function savedSubset(q: SearchQuery): string {
  return serializeSearchParams({ ...q, q: "", page: 1, depth: "normal" }).toString();
}

export function saveFilters(q: SearchQuery): void {
  try {
    const p = savedSubset(q);
    if (p) localStorage.setItem(FILTERS_KEY, p);
    else localStorage.removeItem(FILTERS_KEY);
  } catch {
    // ignore
  }
}

export type FilterSync = "restore" | "save" | "none";

/**
 * What the search page does with the saved filters when its URL changes.
 * - selfNav: the URL was written by the page's own controls (chip, sort, submit, paging) — the
 *   user chose it, so remember it, even when it is bare (they went back to the defaults).
 * - bare arrival from outside (nav link, logo, first visit): start from the saved filters.
 * - non-bare arrival that carries no filters (e.g. "/?q=x" from the viewer's back button): keep
 *   what was saved; overwriting it with nothing would forget the user's preference.
 */
export function decideFilterSync(input: {
  bare: boolean;
  selfNav: boolean;
  hasSaved: boolean;
  hasFilters: boolean;
}): FilterSync {
  if (input.selfNav) return "save";
  if (input.bare) return input.hasSaved ? "restore" : "none";
  return input.hasFilters ? "save" : "none";
}
